import OnDemand from '@/components/ui/PDP/ECommBox/OnDemand';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useGetChildrenQuery } from '@/redux/api/contentDeliveryAPI';
import { OnDemandWebVariation } from '@/@types/content';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  getExpiredStatus,
  getOnDemandProgramRuntime,
  getPriceFromContent,
  getProductImageUrl
} from '../PDP/helpers';
import {
  OnDemandProgramVariants,
  OnDemandProgramVariation,
  sortSegments
} from '@/hooks/PDP/useProgramCatalogRelations';
import useEcommPanelData, { EcommPanelParams } from './hooks/useEcommData';
import { transformIfPLIUrl } from '@/utils/helpers';
import { AnalyticsContext } from '@/analytics/constants';
import useCatalogLabels from '@/hooks/PDP/useCatalogLabels';
import Loading from '../PDP/ECommBox/OnDemand/components/Loading';
import { isContentType, isOfContentType } from '@/lib/helpers/contentType';
import { useLibraryItems } from '@/hooks/PDP/useLibraryItems';
import { usePDPSliceSelectorContext } from '@/hooks/shared/usePDPSliceSelector';
import {
  resetPDPContext,
  setCatalogRelations,
  setHardSelectedVariant,
  setSoftSelectedVariant
} from '@/redux/slices/pdpSlice';
import { ItemListAnalyticsModel } from '@/@types/client-api';
import { useGetPoecProgramsForRenewalQuery } from '@/redux/api/client/library';
import { useAuth } from 'react-oidc-context';
import { createPoecExtensionData } from '@/hooks/PDP/useProgramVariantSelection';

type EcommPanelOnDemandProgramProps = {
  params: EcommPanelParams;
  onClose: () => void;
  context?: AnalyticsContext;
  setIsLoading?: (loading?: boolean) => void;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
};

const EcommPanelOnDemandProgram = ({
  params,
  onClose,
  context,
  setIsLoading,
  itemListAnalyticsModel
}: EcommPanelOnDemandProgramProps) => {
  const auth = useAuth();
  const { sliceName } = usePDPSliceSelectorContext();

  const dispatch = useDispatch();

  const { data, isLoading, isError: isEcommDataError } = useEcommPanelData(params);

  const { onDemandEcomLabels, isLoading: isLabelsLoading } = useCatalogLabels();

  const { data: poecRenewalItems } = useGetPoecProgramsForRenewalQuery(undefined, {
    skip: !auth.isAuthenticated
  });

  const programVariants = useMemo(() => {
    const variants = (data?.filter(v => isOfContentType(v, OnDemandProgramVariants)) ??
      []) as OnDemandProgramVariation[];
    return variants;
  }, [data]);

  const programPks = useMemo(() => programVariants.map(pv => pv.code!), [programVariants]);

  const { hydrateLibrary, selectLibraryItems, isLibraryError } = useLibraryItems();

  useEffect(() => {
    hydrateLibrary(programPks);
  }, [hydrateLibrary, programPks]);
  const ecomLibraryItems = selectLibraryItems(programPks);

  const segmentSet = useMemo(
    () => data?.filter(c => isContentType(c, 'SegmentSetNode'))[0],
    [data]
  );

  const {
    data: segmentProductVariants,
    isFetching: isLoadingSegmentProductVariants,
    isError: isChildrenError,
    isLoading: isChildrenLoading
  } = useGetChildrenQuery(
    segmentSet?.contentLink?.guidValue
      ? { contentLink: segmentSet?.contentLink?.guidValue }
      : skipToken
  );

  useEffect(() => {
    if (!!isEcommDataError || !!isChildrenError || !!isLibraryError) throw Error;
  }, [isChildrenError, isEcommDataError, isLibraryError]);

  useEffect(() => {
    !!setIsLoading && setIsLoading(!!isLoading || !!isLabelsLoading || !!isChildrenLoading);
  }, [isChildrenLoading, isLabelsLoading, isLoading, setIsLoading]);

  const webSegmentsFromProgram = useMemo(
    () =>
      (segmentProductVariants?.children.filter(seg => isContentType(seg, 'OnDemandWebVariation')) ??
        []) as OnDemandWebVariation[],
    [segmentProductVariants?.children]
  );

  const variants = useMemo(
    () => ({
      variants: programVariants.map(variant => {
        const segments = webSegmentsFromProgram
          .filter(seg => seg.liveProgramId === variant.liveProgramId)
          .sort(sortSegments);
        const imageUrl = getProductImageUrl(segments.length ? segments[0].code! : variant.code!);
        const priceInfo = getPriceFromContent(variant);
        const libraryInfo = ecomLibraryItems?.find(l => l.pk === variant.code);
        return {
          code: variant.code!,
          location: variant.location ?? '',
          publicationDate: variant.publicationDate,
          displayName: variant.displayName ?? '',
          itemClass: variant.itemClassId,
          retailPrice: priceInfo.rawRetailPrice,
          discountPrice: priceInfo.rawDiscountPrice,
          price: priceInfo.price,
          originalPrice: priceInfo.strikePrice,
          retail: priceInfo.retail,
          isExpired: getExpiredStatus(variant),
          thumbnail: imageUrl,
          duration: getOnDemandProgramRuntime(variant.runTimeSeconds).thumbnail,
          purchased: libraryInfo?.isPurchased,
          hasMaterials: false,
          materials: undefined,
          poecExtensionData: auth.isAuthenticated
            ? createPoecExtensionData(variant, poecRenewalItems)
            : undefined,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          launchUrl: libraryInfo?.isPurchased ? (variant as any)?.launchUrl : undefined
        };
      })
    }),
    [
      auth.isAuthenticated,
      ecomLibraryItems,
      poecRenewalItems,
      programVariants,
      webSegmentsFromProgram
    ]
  );

  useEffect(() => {
    dispatch(resetPDPContext(sliceName));
    return () => {
      dispatch(resetPDPContext(sliceName));
    };
  }, [dispatch, sliceName]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const selected = variants.variants.find(variant => variant.code === params.pk)!;

    if (variants.variants.length === 1)
      dispatch(setHardSelectedVariant({ sliceName, variant: selected }));
    else dispatch(setSoftSelectedVariant({ sliceName, variant: selected }));
    dispatch(setCatalogRelations({ sliceName, catalogRelations: variants }));
  }, [dispatch, data, params, variants, sliceName]);

  if (isLoading || isLoadingSegmentProductVariants || isLabelsLoading) {
    return <Loading />;
  }

  if (!data) {
    return null;
  }

  return (
    <OnDemand
      onClosePanel={onClose}
      labels={onDemandEcomLabels}
      title={params.title}
      seeDetailsLink={transformIfPLIUrl(params.url)}
      context={context}
      creditTrackerCode={params.creditTrackerCode}
      itemListAnalyticsModel={itemListAnalyticsModel}
    />
  );
};

export default EcommPanelOnDemandProgram;
